import { z } from 'zod'
import {
  getLocalStorage,
  readStorageJson,
  type SafeStorage,
  writeStorageJson,
} from '~/lib/local-storage'

export interface RecentsStore<T> {
  read: (storage?: SafeStorage) => T[]
  write: (list: T[], storage?: SafeStorage) => void
  add: (list: T[], entry: T) => T[]
}

export function recentsStore<T>({
  key,
  schema,
  identity,
  max = 3,
}: {
  key: string
  schema: z.ZodType<T>
  identity: (entry: T) => string
  max?: number
}): RecentsStore<T> {
  const listSchema = z.array(z.unknown()).transform((entries) =>
    entries
      .flatMap((entry) => {
        const result = schema.safeParse(entry)
        return result.success ? [result.data] : []
      })
      .slice(0, max),
  )
  return {
    read: (storage = getLocalStorage()) => {
      const result = listSchema.safeParse(readStorageJson(storage, key))
      return result.success ? result.data : []
    },
    write: (list, storage = getLocalStorage()) => {
      writeStorageJson(storage, key, list)
    },
    add: (list, entry) => {
      const id = identity(entry)
      return [entry, ...list.filter((recent) => identity(recent) !== id)].slice(0, max)
    },
  }
}
